/**
 * MobileFASwapSheet — FRAME 5 (DD-MOBILE-OPTIMIZER-0724). Bottom sheet.
 *
 * A pickup at a full roster IS a swap: incoming + the forced drop it requires + the net
 * category effect + net standings delta — never a bare "add". Binds to the server-authoritative
 * fields (Phase 2): rec.player, rec.drop (enriched), rec.net_category_effect[], rec.score.
 */
import React, { useState } from 'react';
import { leaguesAPI } from '../../../../services/apiService';
import { invalidateLeaguePlayers } from '../../components/player/hooks/playerDataCache';
import MobilePlayerLink from '../../components/MobilePlayerLink';
import MobileCategoryEffect from './MobileCategoryEffect';
import {
  C, SP, FS, R, PRIMARY, BORDER, BORDER_SOFT, NUM, withAlpha, sectionHeaderStyle,
} from './optimizerTokens';

function Sheet({ title, onClose, children, footer }) {
  const s = sectionHeaderStyle(PRIMARY);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
         data-testid="fa-swap-sheet" role="dialog" aria-modal="true">
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: withAlpha(C.black || '#000', 0.55) }} />
      <div style={{ position: 'relative', background: C.neutral[900], borderTop: BORDER, maxHeight: '82vh', display: 'flex', flexDirection: 'column', ...NUM }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: `7px 0 5px` }}>
          <div style={{ width: 36, height: 3, borderRadius: R.none, background: C.neutral[600] }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${SP.sm} 9px`, gap: SP.xs }}>
          <div style={{ flex: 1, fontSize: FS.base, fontWeight: 900, color: C.white }}>{title}</div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="fa-swap-close"
            style={{ width: 26, height: 26, border: BORDER, borderRadius: R.none, background: 'none', color: C.neutral[400], cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', padding: `0 ${SP.sm} ${SP.sm}` }}>{children}</div>
        {footer}
      </div>
      <style>{`[data-testid="fa-swap-sheet"] ::-webkit-scrollbar{display:none;}`}</style>
    </div>
  );
}

function fmtStat(k, v) {
  if (v == null) return null;
  if (k === 'AVG' || k === 'ERA') return `${v} ${k}`;
  return `${v} ${k}`;
}

export default function MobileFASwapSheet({ rec, leagueId, onClose, onComplete, onPlayerClick }) {
  const [submitting, setSubmitting] = useState(false);
  const [swapError, setSwapError] = useState(null);
  if (!rec) return null;
  const add = rec.player || {};
  const drop = rec.drop || {};

  // DD-MOBILE-OPTIMIZER-ACTIONS-0724: execute the add+drop through the app's canonical ATOMIC
  // plan endpoints (generatePlanForDrop → executePlan — the same drop-shuffle useRosterPlan
  // wraps; Rule 11, no hand-rolled mutation). The optimizer already carries the
  // server-authoritative drop, so no interactive drop-designation is needed. §4.5.10.2: the
  // execute-plan router invalidates the ownership caches server-side; we invalidate the mobile
  // modal cache here and RE-FETCH the optimizer (a stale post-swap result is this screen's
  // Ty-France bug). §4.5.21: fail LOUD on error — no silent no-op, no partial half-state (the
  // plan executes atomically and rolls back server-side on failure).
  const confirmSwap = async () => {
    const candidateId = add?.ids?.mlb;
    const dropId = drop?.player_id;
    if (candidateId == null || dropId == null) {
      setSwapError('This swap is missing the player it adds or drops.');
      return;
    }
    setSubmitting(true);
    setSwapError(null);
    try {
      const planRes = await leaguesAPI.generatePlanForDrop(leagueId, {
        candidate_player_id: candidateId, drop_player_id: dropId,
      });
      const plan = (planRes?.plans || [])[0];
      if (!plan) throw new Error(planRes?.infeasible_reason || 'No executable plan for this swap.');
      const exec = await leaguesAPI.executePlan(leagueId, {
        candidate_player_id: candidateId, drop_player_id: dropId,
        plan_fingerprint: plan.plan_fingerprint,
      });
      if (exec && exec.success === false) {
        throw new Error(exec.error_message || 'Swap failed.');
      }
      invalidateLeaguePlayers(leagueId);      // §4.5.10.2 frontend modal cache
      if (onComplete) onComplete();           // re-fetch the optimizer (stale-result fix)
      onClose();
    } catch (e) {
      setSwapError(e?.response?.data?.detail || e?.message || 'Swap failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  // §4.5.21 — a swap REQUIRES both sides. A rec without a server-authoritative forced drop is
  // malformed; fail LOUD and visible rather than render a bare "add" (the exact honesty defect
  // the swap sheet exists to prevent). Never a disabled-looking Confirm over half a swap.
  if (!drop.player_name) {
    return (
      <Sheet title={`Add ${add.info?.full_name || 'player'}`} onClose={onClose}>
        <div style={{ padding: `${SP.lg} 0`, textAlign: 'center' }} data-testid="fa-swap-malformed">
          <div style={{ fontSize: FS.sm, fontWeight: 700, color: C.error }}>Can’t show this swap</div>
          <div style={{ fontSize: FS.xs, color: C.neutral[400], marginTop: SP.xs, lineHeight: 1.5 }}>
            The optimizer didn’t return the forced drop for this pickup. A pickup at a full roster
            is a swap — it can’t be confirmed without the player it drops.
          </div>
        </div>
      </Sheet>
    );
  }
  const nce = rec.net_category_effect || [];
  const net = rec.score?.net_standings_delta ?? rec.score?.total_points_delta;
  const addName = add.info?.full_name || 'Free agent';
  const dropStat = drop.stat_line || {};
  const dropLine = Object.keys(dropStat).filter((k) => k !== 'side')
    .map((k) => fmtStat(k, dropStat[k])).filter(Boolean).slice(0, 3).join(' · ');

  return (
    <Sheet title={`Add ${addName}`} onClose={onClose}
      footer={
        <div>
          {swapError && (
            <div style={{ padding: `8px ${SP.sm}`, background: withAlpha(C.error, 0.1), borderTop: `1px solid ${C.error}`, color: C.error, fontSize: FS.xs }}
                 data-testid="swap-error" role="alert">
              {swapError}
            </div>
          )}
          <div style={{ display: 'flex', borderTop: BORDER, background: C.neutral[900] }}>
            <button type="button" onClick={onClose} disabled={submitting}
              style={{ flex: 1, textAlign: 'center', padding: `13px 0`, color: C.neutral[400], fontWeight: 700, fontSize: FS.xs, background: 'none', border: 'none', cursor: submitting ? 'default' : 'pointer' }}>Cancel</button>
            <button type="button" onClick={confirmSwap} disabled={submitting} data-testid="confirm-swap"
              style={{ flex: 1.4, textAlign: 'center', padding: `13px 0`, color: C.neutral[950], fontWeight: 800, fontSize: FS.xs, background: PRIMARY, borderLeft: BORDER, border: 'none', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Confirming…' : 'Confirm Swap'}
            </button>
          </div>
        </div>
      }>
      {/* swap band: ADD ⇄ DROP (enriched drop — position/status/salary/stats) */}
      <div style={{ display: 'flex', alignItems: 'stretch', borderTop: BORDER, borderBottom: BORDER }} data-testid="swap-band">
        <div style={{ flex: 1, padding: `9px ${SP.sm}` }}>
          <div style={{ fontSize: FS['2xs'], fontWeight: 800, color: C.success }}>ADD</div>
          <MobilePlayerLink mlbId={add.ids?.mlb} name={addName} onClick={onPlayerClick}
            style={{ fontSize: FS.sm, fontWeight: 700, color: C.white }} />
          <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{add.eligibility_display || add.info?.position} · {add.info?.mlb_team} · FA</div>
        </div>
        <div style={{ width: 34, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: BORDER_SOFT, borderRight: BORDER_SOFT, color: PRIMARY, fontSize: FS.lg }}>⇄</div>
        <div style={{ flex: 1, padding: `9px ${SP.sm}`, textAlign: 'right' }} data-testid="forced-drop">
          <div style={{ fontSize: FS['2xs'], fontWeight: 800, color: C.error }}>DROP</div>
          <MobilePlayerLink mlbId={drop.player_id} name={drop.player_name || '—'} onClick={onPlayerClick}
            style={{ fontSize: FS.sm, fontWeight: 700, color: C.neutral[300] }} />

          <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>
            {[(drop.eligibility_display || drop.position), drop.roster_status, drop.salary != null ? `$${drop.salary}` : null].filter(Boolean).join(' · ')}
          </div>
          {dropLine && <div style={{ fontSize: FS['2xs'], color: C.neutral[400], marginTop: 2, ...NUM }}>{dropLine}</div>}
        </div>
      </div>

      {/* NET CATEGORY EFFECT — the surfaced per-category rows (shared with the trade detail modal) */}
      <MobileCategoryEffect rows={nce.slice(0, 6)} />

      {/* net standings delta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SP.xs, marginTop: SP.sm, padding: `9px ${SP.sm}`,
        background: withAlpha(C.success, 0.08), borderTop: BORDER, borderBottom: BORDER, margin: `${SP.sm} -${SP.sm} 0` }}
        data-testid="net-standings-delta">
        <div style={{ fontSize: FS.xl, fontWeight: 900, color: net >= 0 ? C.success : C.error, ...NUM }}>{net >= 0 ? `+${net}` : net}</div>
        <div style={{ fontSize: FS.xs, color: C.neutral[300] }}>net standings points</div>
      </div>
    </Sheet>
  );
}
